import {
    ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit,
    ViewEncapsulation, AfterContentInit, ContentChildren, ChangeDetectorRef,
} from '@angular/core';
import { FormMessageComponent } from './form-message.component';
import { FormGroup, AbstractControl } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { isNullOrUndefined } from '@core/utils/object';
import { IHDValidationErrors } from '@core/models/form/validation/form-validation-errors';
/**
 * Manages form error messages via the form control status.
 * Form messages are defined as children and map to validators via the `name` attribute.
 */
@Component({
    selector: 'ihd-form-messages',
    template: '<ng-content></ng-content>',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    styleUrls: [
        './form-messages.component.scss'
    ],
    host: {
        class: 'c-form-messages'
    }
})
export class FormMessagesComponent implements OnInit, OnDestroy, AfterContentInit {

    @ContentChildren(FormMessageComponent) public formMessages;
    @Input() public form: FormGroup;
    @Input() public name: string;
    @Input() public set submitted(value) {
        this._submitted = value;
        if (this.submitted) {
            this.renderMessages();
        }
    }

    public get submitted(): boolean {
        return this._submitted;
    }

    private destroyed$: Subject<void> = new Subject();
    private formControl: AbstractControl;
    private _submitted: boolean;

    constructor(private cdr: ChangeDetectorRef) {
    }

    /**
     * Renders form control messages based on errors.
     */
    protected renderMessages() {
        if (this.formControl) {
            // Define list of errors
            let formErrors: string[] = [];

            if (this.submitted === false && !this.formControl.valid) {
              this.formControl.markAsUntouched();
            } else {
              this.formControl.markAsTouched();
            }

            if (!this.formControl.valid && this.formControl.errors &&
              ((this.formControl.dirty && isNullOrUndefined(this.submitted)) || ( this.submitted))
            ) {
                formErrors = Object.keys(this.formControl.errors);
            }

            // Validate the form messages
            if(this.formMessages){
                this.formMessages.forEach((formMessage: FormMessageComponent) => {
                    const error: IHDValidationErrors = this.formControl.errors && this.formControl.errors[formMessage.name];
                    formMessage.hasError = (formErrors.indexOf(formMessage.name) >= 0 && !isNullOrUndefined(error));

                    if (formMessage.hasError && error && error.translationKeyValues) {
                        formMessage.setTranslationValues(error.translationKeyValues);
                    }
                });
            }

            this.cdr.markForCheck();
        }
    }

    ngAfterContentInit() {

        // Reset the form messages
        this.formMessages.forEach((formMessage: FormMessageComponent) => {
            formMessage.hasError = false;
        });
        this.renderMessages();
    }

    ngOnInit() {
        if (this.form) {
            if (this.name && this.form.controls.hasOwnProperty(this.name)) {
                this.formControl = this.form.controls[this.name] as AbstractControl;

            } else {

                // Subscribe to form / AbstractControl directly
                this.formControl = this.form as AbstractControl;
            }

            this.formControl.statusChanges
              .pipe(takeUntil(this.destroyed$))
              .subscribe(() => {
                  this.renderMessages();
              });
        }
    }

    ngOnDestroy() {
        this.destroyed$.next();
    }
}
